// Copyright 2022-2026 - Hewlett Packard Enterprise Company
import { useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  Box,
  Button,
  Heading,
  Layer,
  Text,
  Paragraph,
  Anchor,
  Notification,
} from 'grommet';
import PropTypes from 'prop-types';
import useFlags from '../../hooks/useFlags';
import { CONTENT_IDS } from '../../constants/helpContentIds';
import {
  DISABLED_CONDITIONS,
  COM_OPS_URL,
  CONNECTION_TYPE_ENUM,
  MAGIC_LINK_CLAIM_TYPE,
} from '../../constants';
import { getDisabledServerCondition, isOvServer } from '../../utils/apiHelpers';
import LearnMoreLink from '../LearnMoreLink/LearnMoreLink';

const SubscriptionAnchorContent = (
  <Anchor
    label={COM_OPS_URL}
    target="_blank"
    rel="noreferrer noopener"
    href={COM_OPS_URL}
  />
);

export default function DeviceDisabledModal({ data, onClose, onRemove }) {
  const { t } = useTranslation('common');
  const flags = useFlags();
  const magicLinkEnabled =
    flags.T_20251030_660_COM_SETS_PCID_ON_ILO_AUTO_CONNECT_BOOL();
  const [condition, setCondition] = useState();

  const content = {};

  const isOvDevice = isOvServer(data);
  const isThirdPartyServer =
    data?.connectionType === CONNECTION_TYPE_ENUM.CREDENTIAL;
  const isMagicLinkServer = data?.deviceClaimType === MAGIC_LINK_CLAIM_TYPE;
  content[DISABLED_CONDITIONS.NOT_ASSIGNED] = {
    condition: t('dev_disabled_modal.not_assigned_cond'),
    resolution: t('dev_disabled_modal.not_assigned_res'),
  };
  content[DISABLED_CONDITIONS.NOT_ACTIVATED] = {
    condition: t('dev_disabled_modal.not_activ_cond'),
    resolution:
      isMagicLinkServer && magicLinkEnabled
        ? t('dev_disabled_modal.not_activ_magiclink_res')
        : t('dev_disabled_modal.not_activ_res'),
    helpLabel: t('dev_disabled_modal.learn_more_activation'),
  };
  content[DISABLED_CONDITIONS.SUBSCRIPTION_REQUIRED] = {
    condition: t('dev_disabled_modal.sub_req_cond'),
    resolution: (
      <Trans
        t={t}
        i18nKey="dev_disabled_modal.sub_req_res"
        components={{
          subscriptionLink: SubscriptionAnchorContent,
        }}
      />
    ),
    helpLabel: t('dev_disabled_modal.learn_more_subs'),
  };
  content[DISABLED_CONDITIONS.SUBSCRIPTION_EXPIRED] = {
    condition: isOvDevice
      ? t('dev_disabled_modal.ov_sub_exp_cond')
      : t('dev_disabled_modal.sub_exp_cond'),
    resolution: (
      <Trans
        t={t}
        i18nKey={
          isOvDevice
            ? 'dev_disabled_modal.ov_sub_exp_res'
            : 'dev_disabled_modal.sub_exp_res'
        }
        components={{
          subscriptionLink: SubscriptionAnchorContent,
        }}
      />
    ),
    helpLabel: t('dev_disabled_modal.learn_more_subs'),
  };

  useEffect(() => {
    setCondition(getDisabledServerCondition(data));
  }, [data]);

  return (
    <Layer
      as="section"
      position="center"
      modal
      onClickOutside={onClose}
      onEsc={onClose}
    >
      <Box gap="none" pad="medium" width="586px">
        <>
          <Heading
            level={2}
            margin={{ top: 'none', bottom: 'medium' }}
            truncate
          >
            {data.name}
          </Heading>

          {condition ? (
            <Box>
              <Notification
                status="warning"
                pad={{ vertical: 'xsmall' }}
                message={
                  <Text size="large">{content[condition].condition}</Text>
                }
              />
              <Paragraph>
                {isThirdPartyServer ? (
                  <Text>{t('dev_disabled_modal.sub_req_res_third_party')}</Text>
                ) : (
                  <Text>{content[condition].resolution}</Text>
                )}
              </Paragraph>
              {content[condition].helpLabel && (
                <LearnMoreLink
                  label={content[condition].helpLabel}
                  topicId={CONTENT_IDS.STATUS_VALUES}
                />
              )}
            </Box>
          ) : (
            <Box />
          )}
        </>
        <Box justify="end" direction="row" gap="medium">
          {isThirdPartyServer && (
            <Button
              label={t('dev_disabled_modal.remove_server_btn')}
              onClick={onRemove}
              secondary
            />
          )}
          <Button
            label={t('dev_disabled_modal.close_btn')}
            onClick={onClose}
            primary
          />
        </Box>
      </Box>
    </Layer>
  );
}

DeviceDisabledModal.propTypes = {
  data: PropTypes.object,
  onClose: PropTypes.func,
  onRemove: PropTypes.func,
};
